import {normalizeCapabilityMap, WORKSPACE_CMS_CAPABILITIES} from '@/utils/access-capabilities';
import {
    defaultHomePathForTemplateFamily,
    deriveTemplateFamily,
    deriveTemplateProfile,
    TEMPLATE_FAMILIES,
    TEMPLATE_PROFILES,
} from '@/utils/template-profiles';

const FLAG_DEFAULTS = {
    customer_positions_enabled: true,
    customer_timeline_enabled: true,
    customer_file_manager_enabled: true,
    customer_documentations_enabled: true,
    customer_boards_enabled: false,
    customer_relationships_enabled: false,
    customer_finances_enabled: false,
    customer_vault_enabled: false,
    customer_server_monitoring_enabled: false,
    customer_cms_enabled: false,
};

function normalizeBoolean(value, fallback = false) {
    if (value === true || value === 1 || value === '1') {
        return true;
    }
    if (value === false || value === 0 || value === '0') {
        return false;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') {
            return true;
        }
        if (normalized === 'false' || normalized === '') {
            return false;
        }
    }

    if (value === null || value === undefined) {
        return !!fallback;
    }

    return !!value;
}

function normalizeObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function normalizeGroup(value) {
    return String(value || '').trim().toLowerCase();
}

function normalizeTemplateValue(value) {
    return String(value || '').trim().toLowerCase();
}

export function deriveRoleGroup(user = null, access = {}) {
    const source = normalizeObject(access);
    const directGroup = normalizeGroup(
        source.group
        || (user && user.role && user.role.slug)
        || (user && user.role_slug)
        || (user && user.slug)
    );

    return directGroup || '';
}

function resolveWorkspaceAccess(user = null, access = {}) {
    const source = normalizeObject(access);
    const role = user && user.role ? user.role : {};

    return normalizeBoolean(
        source.workspace_access,
        normalizeBoolean(source.dashboard_access, normalizeBoolean(role.workspace_access, normalizeBoolean(role.dashboard_access, false)))
    );
}

function resolveCapabilities(user = null, access = {}) {
    const source = normalizeObject(access);
    const accessCapabilities = normalizeCapabilityMap(source.capabilities);
    if (Object.keys(accessCapabilities).length > 0) {
        return accessCapabilities;
    }

    return normalizeCapabilityMap(user && user.role ? user.role.permissions : {});
}

function deriveProjectOverviewAccess(user = null, group = '') {
    if (group === 'customer') {
        return Array.isArray(user && user.project_ids) && user.project_ids.some((projectId) => Number(projectId) > 0);
    }

    return group !== 'guest' && group !== '';
}

function resolveFlags(user = null, access = {}, group = '', isAccountUser = false) {
    const accessFlags = normalizeObject(normalizeObject(access).flags);
    const flags = {};

    Object.keys(FLAG_DEFAULTS).forEach((key) => {
        if (accessFlags[key] !== undefined) {
            flags[key] = normalizeBoolean(accessFlags[key], FLAG_DEFAULTS[key]);
            return;
        }

        const userValue = user ? user[key] : undefined;
        flags[key] = normalizeBoolean(userValue, FLAG_DEFAULTS[key]);
    });

    if (group !== 'customer' && group !== 'guest') {
        flags.customer_positions_enabled = false;
        flags.customer_timeline_enabled = false;
        flags.customer_file_manager_enabled = false;
        flags.customer_finances_enabled = false;
    }

    if (group !== 'customer' && group !== 'guest') {
        flags.customer_documentations_enabled = false;
    }

    if (group === 'guest' || (group !== 'customer' && group !== 'employee' && group !== 'service_provider' && !isAccountUser)) {
        flags.customer_boards_enabled = false;
        flags.customer_relationships_enabled = false;
    }

    if (group !== 'customer' && group !== 'guest' && !isAccountUser) {
        flags.customer_vault_enabled = false;
        flags.customer_server_monitoring_enabled = false;
    }

    if (group !== 'employee' && group !== 'service_provider') {
        flags.customer_cms_enabled = false;
    }

    return flags;
}

function deriveModules(user = null, access = {}, group = '', flags = {}, capabilities = {}, isAccountUser = false) {
    const backendModules = normalizeObject(normalizeObject(access).modules);
    const isAdmin = group === 'admin';
    const isCustomer = group === 'customer';
    const isGuest = group === 'guest';
    const isServiceProvider = group === 'service_provider';
    const isEmployeeLike = group === 'employee' || group === 'service_provider';
    const projectOverviewAccess = deriveProjectOverviewAccess(user, group);
    const hasCmsCapability = WORKSPACE_CMS_CAPABILITIES.some((capability) => !!capabilities[capability]);

    const moduleValue = function (key, fallback) {
        return backendModules[key] !== undefined ? !!backendModules[key] : fallback;
    };

    const modules = {
        tickets: moduleValue('tickets', isCustomer || isGuest || isEmployeeLike || !!capabilities['workspace.tickets']),
        projects: moduleValue('projects', projectOverviewAccess),
        relationships: moduleValue(
            'relationships',
            !isGuest && (isAdmin || ((isCustomer || isEmployeeLike) && flags.customer_relationships_enabled))
        ),
        positions: moduleValue('positions', isServiceProvider ? true : (isCustomer ? flags.customer_positions_enabled : (!isGuest && !!capabilities['workspace.positions']))),
        timeline: moduleValue('timeline', isServiceProvider ? true : (isCustomer ? flags.customer_timeline_enabled : (!isGuest && !!capabilities['workspace.positions']))),
        file_manager: moduleValue('file_manager', (isCustomer || isGuest) ? flags.customer_file_manager_enabled : (!!capabilities['workspace.file_manager'])),
        documentation: moduleValue('documentation', isCustomer ? flags.customer_documentations_enabled : !!capabilities['workspace.documentation']),
        customer_documentation: moduleValue('customer_documentation', (isCustomer || isGuest) && flags.customer_documentations_enabled),
        boards: moduleValue('boards', isGuest
            ? false
            : (isAdmin
                ? true
                : !!flags.customer_boards_enabled)),
        finance: moduleValue('finance', isServiceProvider ? true : (isCustomer ? flags.customer_finances_enabled : !!capabilities['workspace.finance'])),
        vault: moduleValue('vault', isAdmin
            ? true
            : ((isCustomer || isGuest || isAccountUser)
                ? flags.customer_vault_enabled
                : !!capabilities['workspace.vault'])),
        insights: moduleValue('insights', isCustomer
            ? flags.customer_server_monitoring_enabled
            : (!!capabilities['workspace.server_monitoring'] || (isAccountUser && flags.customer_server_monitoring_enabled))),
        server_monitoring: moduleValue('server_monitoring', isCustomer
            ? flags.customer_server_monitoring_enabled
            : (!!capabilities['workspace.server_monitoring'] || (isAccountUser && flags.customer_server_monitoring_enabled))),
        admin: moduleValue('admin', isAdmin),
        admin_users: moduleValue('admin_users', isAdmin && !!capabilities['workspace.admin.users']),
        companies: moduleValue('companies', isAdmin && !!capabilities['workspace.admin.companies']),
        cms: moduleValue('cms', isAdmin || hasCmsCapability || (isEmployeeLike && !!flags.customer_cms_enabled)),
        settings: moduleValue('settings', isAdmin && !!capabilities['workspace.admin.settings']),
    };

    if (!isCustomer && !modules.positions) {
        modules.timeline = false;
    }

    return modules;
}

export function normalizeAccessContext(rawAccess = {}, user = null) {
    const access = normalizeObject(rawAccess);
    const group = deriveRoleGroup(user, access) || null;
    const workspaceAccess = resolveWorkspaceAccess(user, access);
    const isAdmin = normalizeBoolean(access.is_admin, group === 'admin');
    const isCustomer = normalizeBoolean(access.is_customer, group === 'customer');
    const isGuest = normalizeBoolean(access.is_guest, group === 'guest');
    const isEmployeeLike = normalizeBoolean(access.is_employee_like, group === 'employee' || group === 'service_provider');
    const isServiceProvider = normalizeBoolean(access.is_service_provider, group === 'service_provider');
    const isAccountUser = normalizeBoolean(
        access.is_account_user,
        group === 'customer' || group === 'employee' || group === 'service_provider' || group === 'guest'
    );
    const templateProfile = normalizeTemplateValue(access.template_profile)
        || deriveTemplateProfile({
            group: group || '',
            isAdmin,
            isServiceProvider,
            isEmployeeLike,
            isCustomer,
            isGuest,
        });
    const templateFamily = normalizeTemplateValue(access.template_family)
        || deriveTemplateFamily({
            templateProfile,
            workspaceAccess,
        });
    const capabilities = resolveCapabilities(user, access);
    const flags = resolveFlags(user, access, group || '', isAccountUser);
    const modules = deriveModules(user, access, group || '', flags, capabilities, isAccountUser);
    const homePath = String(access.home_path || '').trim() || defaultHomePathForTemplateFamily(templateFamily);
    const experienceFallback = templateFamily === TEMPLATE_FAMILIES.INTERNAL ? 'workspace' : templateFamily;

    return {
        group,
        experience: String(access.experience || experienceFallback),
        template_family: templateFamily,
        template_profile: templateProfile,
        home_path: homePath,
        workspace_access: workspaceAccess,
        is_admin: isAdmin,
        is_customer: isCustomer,
        is_guest: isGuest,
        is_employee_like: isEmployeeLike,
        is_service_provider: isServiceProvider,
        is_account_user: isAccountUser,
        capabilities,
        flags,
        modules,
    };
}

export function deriveAccessContext({user = null} = {}) {
    return normalizeAccessContext(user && user.access ? user.access : {}, user);
}

export function customerHomePath(access) {
    const normalizedAccess = normalizeObject(access);
    const templateFamily = normalizeTemplateValue(normalizedAccess.template_family)
        || (normalizedAccess.workspace_access ? TEMPLATE_FAMILIES.INTERNAL : TEMPLATE_FAMILIES.CUSTOMER);

    return String(normalizedAccess.home_path || '').trim() || defaultHomePathForTemplateFamily(templateFamily);
}

export function resolveStoreAccess(store) {
    if (store && store.state && store.state.access) {
        return normalizeAccessContext(store.state.access, store.state.user || null);
    }

    return deriveAccessContext({
        user: store && store.state ? store.state.user : null,
    });
}

export function createAccessHelpers(store) {
    const resolve = () => resolveStoreAccess(store);

    return {
        context() {
            return resolve();
        },
        group() {
            return resolve().group || '';
        },
        templateFamily() {
            return resolve().template_family || TEMPLATE_FAMILIES.PUBLIC;
        },
        templateProfile() {
            return resolve().template_profile || TEMPLATE_PROFILES.PUBLIC;
        },
        isAdmin() {
            return !!resolve().is_admin;
        },
        isCustomer() {
            return !!resolve().is_customer;
        },
        isGuest() {
            return !!resolve().is_guest;
        },
        isServiceProvider() {
            return !!resolve().is_service_provider;
        },
        isEmployeeLike() {
            return !!resolve().is_employee_like;
        },
        workspaceAccess() {
            return !!resolve().workspace_access;
        },
        hasModule(moduleKey) {
            return !!resolve().modules[moduleKey];
        },
        hasCapability(capabilityKey) {
            return !!resolve().capabilities[capabilityKey];
        },
        customerHomePath() {
            return customerHomePath(resolve());
        },
    };
}
